import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { AuthShell } from "./AuthShell";
import { useAuth } from "@/lib/auth";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { useGoogleLogin } from "@react-oauth/google";

export default function Login() {
    const navigate = useNavigate();
    const location = useLocation();
    const { login, loginWithGoogle, getErrorMessage } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPwd, setShowPwd] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const loginGoogle = useGoogleLogin({
        onSuccess: async (res) => {
            setError(""); setLoading(true);
            try {
                await loginWithGoogle(res.access_token);
                toast.success("Welcome back!");
                const dest = location.state?.from || "/dashboard";
                navigate(dest, { replace: true });
            } catch (err) {
                setError(getErrorMessage(err));
            } finally {
                setLoading(false);
            }
        },
        onError: () => setError("Google Sign-In failed"),
    });

    const onSubmit = async (e) => {
        e.preventDefault();
        setError(""); setLoading(true);
        try {
            await login(email, password);
            toast.success("Welcome back!");
            const dest = location.state?.from || "/dashboard";
            navigate(dest, { replace: true });
        } catch (err) {
            setError(getErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <AuthShell title="Welcome back." subtitle="Sign in to your Audo newsroom dashboard.">
            <form onSubmit={onSubmit} data-testid="login-form" className="space-y-4">
                <button
                    type="button"
                    onClick={() => loginGoogle()}
                    disabled={loading}
                    className="w-full bg-white hover:bg-cream-100 border border-cream-300 text-ink rounded-full py-3.5 text-[14.5px] font-semibold inline-flex items-center justify-center gap-2 transition-all disabled:opacity-70 mb-4"
                >
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                    Continue with Google
                </button>

                <div className="relative flex items-center py-2 mb-4">
                    <div className="flex-grow border-t border-cream-300"></div>
                    <span className="flex-shrink-0 mx-4 text-ink-muted text-[11px] uppercase tracking-wider font-semibold">Or with email</span>
                    <div className="flex-grow border-t border-cream-300"></div>
                </div>

                <div>
                    <label className="block text-[12px] font-semibold text-ink mb-1.5">Email</label>
                    <input
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        data-testid="login-email"
                        placeholder="you@newsroom.com"
                        className="w-full bg-white border border-cream-300 rounded-2xl px-4 py-3 text-[15px] focus:outline-none focus:ring-2 focus:ring-coral/40 focus:border-coral transition"
                    />
                </div>
                <div>
                    <div className="flex items-center justify-between mb-1.5">
                        <label className="block text-[12px] font-semibold text-ink">Password</label>
                        <Link to="/forgot-password" className="text-[12px] text-coral hover:underline" data-testid="forgot-link">Forgot?</Link>
                    </div>
                    <div className="relative">
                        <input
                            type={showPwd ? "text" : "password"}
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            data-testid="login-password"
                            placeholder="••••••••"
                            className="w-full bg-white border border-cream-300 rounded-2xl px-4 py-3 pr-11 text-[15px] focus:outline-none focus:ring-2 focus:ring-coral/40 focus:border-coral transition"
                        />
                        <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted hover:text-ink" aria-label="Toggle password">
                            {showPwd ? <EyeOff size={16}/> : <Eye size={16}/>}
                        </button>
                    </div>
                </div>
                {error && <p className="text-[13px] text-coral font-medium" data-testid="login-error">{error}</p>}
                <button
                    type="submit"
                    disabled={loading}
                    data-testid="login-submit"
                    className="w-full bg-ink hover:bg-coral text-cream-100 rounded-full py-3.5 text-[14.5px] font-semibold inline-flex items-center justify-center gap-2 transition-all disabled:opacity-70"
                >
                    {loading ? <><Loader2 size={16} className="animate-spin"/> Signing in…</> : <>Sign in →</>}
                </button>
                <p className="text-[13px] text-ink-muted text-center pt-2">
                    No account yet? <Link to="/register" className="text-ink font-semibold hover:text-coral" data-testid="link-register">Create one</Link>
                </p>
            </form>
        </AuthShell>
    );
}
